import React, { useEffect, useState } from 'react';
import { Loader2, CheckCircle2, Banknote, Trash2, ChevronDown, X } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { oapi, odownload, fmtMoney, newKey } from '@/lib/officialsApi';
import { BATCH_STATUS, DownloadMenu, Loading, Money, PayStatusBadge, lineTitle } from './PayBits';
import { MethodFields, blankPayment } from './RecordPaymentDialog';

const OfficialRow = ({ r, b, currency, perms, checked, onCheck, open, onOpen, onRemove, busy }) => {
  const payable = b.status === 'approved' && r.remaining_minor > 0 && perms.has('record');
  return (
    <div className="border-t first:border-t-0" data-testid={`batch-official-${r.official_id}`}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2">
        <div className="w-5">{payable && <Checkbox checked={checked} onCheckedChange={() => onCheck(r.official_id)} data-testid={`batch-pay-select-${r.official_id}`} />}</div>
        <button type="button" onClick={onOpen} className="flex-1 min-w-[150px] text-left flex items-center gap-1.5 font-semibold text-slate-800" data-testid={`batch-expand-${r.official_id}`}>
          <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />{r.official_name}
          <span className="text-xs font-normal text-slate-400">· {r.games} game{r.games === 1 ? '' : 's'}</span>
        </button>
        <Money minor={r.amount_minor} currency={currency} className="w-24 text-right font-semibold" />
        <Money minor={r.paid_minor} currency={currency} className="w-24 text-right text-emerald-700" />
        <Money minor={r.remaining_minor} currency={currency} className="w-24 text-right font-bold text-red-600" testId={`batch-official-remaining-${r.official_id}`} />
      </div>
      {open && (
        <ul className="pl-12 pr-3 pb-2 space-y-1">
          {r.lines.map(l => (
            <li key={l.id} className="flex items-center gap-2 text-xs text-slate-600" data-testid={`batch-line-${l.id}`}>
              <span className="w-20 text-slate-400">{l.date}</span><span className="flex-1 truncate">{lineTitle(l)}{l.role_label ? ` · ${l.role_label}` : ''}</span>
              <Money minor={l.amount_minor} currency={currency} className={l.stage === 'void' ? 'line-through text-slate-400' : ''} />
              {b.status === 'draft' && perms.has('approve') && (
                <button type="button" disabled={busy} onClick={() => onRemove(l.id)} className="text-slate-400 hover:text-red-600" title="Remove from batch" data-testid={`batch-remove-line-${l.id}`}><X className="w-3.5 h-3.5" /></button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const BatchDetailDialog = ({ batchId, orgId, currency, perms, onClose, onChanged }) => {
  const [b, setB] = useState(null);
  const [sel, setSel] = useState(new Set());
  const [open, setOpen] = useState(null);
  const [paying, setPaying] = useState(null);
  const [del, setDel] = useState(false);
  const [busy, setBusy] = useState(false);
  const apply = (x) => { setB(x); setSel(new Set(x.officials.filter(r => r.remaining_minor > 0).map(r => r.official_id))); };
  useEffect(() => {
    setB(null); setPaying(null); setDel(false); setOpen(null);
    if (batchId) oapi(`/officials/payments/batches/${batchId}`, { orgId }).then(r => apply(r.batch)).catch(e => toast.error(e.message));
  }, [batchId, orgId]);
  const run = async (fn, msg) => {
    setBusy(true);
    try { const r = await fn(); if (r?.batch) apply(r.batch); toast.success(typeof msg === 'function' ? msg(r) : msg); onChanged(); return r; }
    catch (e) { toast.error(e.message); return null; } finally { setBusy(false); }
  };
  const path = `/officials/payments/batches/${batchId}`;
  const approve = () => run(() => oapi(`${path}/approve`, { method: 'POST', orgId, body: {} }), r => `Batch approved · ${r.approved} item${r.approved === 1 ? '' : 's'}`);
  const removeLine = (id) => run(() => oapi(`${path}/remove-lines`, { method: 'POST', orgId, body: { line_ids: [id] } }), 'Removed from batch');
  const pay = async () => {
    const { key, ...form } = paying;
    const r = await run(() => oapi(`${path}/pay`, { method: 'POST', orgId, body: { ...form, items: [...sel].map(official_id => ({ official_id })), idempotency_key: key } }),
      x => `${x.recorded.length} payment${x.recorded.length === 1 ? '' : 's'} recorded${x.errors.length ? ` · ${x.errors.length} failed: ${x.errors[0].error}` : ''}`);
    if (r) setPaying(null);
  };
  const remove = async () => { if (await run(() => oapi(path, { method: 'DELETE', orgId }), 'Batch deleted')) onClose(); };
  const download = (format) => odownload(`${path}/export`, { org_id: orgId, format }, `officials_batch_${b.from_date}_${b.to_date}.${format}`).catch(e => toast.error(e.message));
  const toggle = (id) => setSel(p => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const selTotal = b ? b.officials.filter(r => sel.has(r.official_id)).reduce((s, r) => s + r.remaining_minor, 0) : 0;
  return (
    <Dialog open={!!batchId} onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl" data-testid="batch-detail-dialog">
        {!b ? <><DialogTitle className="sr-only">Loading batch</DialogTitle><Loading /></> : (
          <>
            <DialogHeader>
              <div className="flex flex-wrap items-center gap-2"><DialogTitle className="text-[#00247D] text-xl" data-testid="batch-detail-name">{b.name}</DialogTitle>
                <PayStatusBadge status={b.display_status} map={BATCH_STATUS} testId="batch-detail-status" /></div>
              <DialogDescription>{b.from_date} → {b.to_date} · Total {fmtMoney(b.total_minor, currency)} · Paid {fmtMoney(b.paid_minor, currency)} · Remaining {fmtMoney(b.remaining_minor, currency)}
                {b.approved_by_name ? ` · Approved by ${b.approved_by_name}` : ''}</DialogDescription>
            </DialogHeader>
            <div className="flex flex-wrap gap-2">
              {b.status === 'draft' && perms.has('approve') && <Button size="sm" disabled={busy} onClick={approve} className="bg-indigo-600 hover:bg-indigo-700" data-testid="batch-approve-btn"><CheckCircle2 className="w-4 h-4 mr-1.5" />Approve batch</Button>}
              {b.status === 'approved' && perms.has('record') && b.remaining_minor > 0 && !paying && (
                <Button size="sm" disabled={!sel.size} onClick={() => setPaying({ ...blankPayment(), key: newKey() })} className="bg-emerald-600 hover:bg-emerald-700" data-testid="batch-pay-btn">
                  <Banknote className="w-4 h-4 mr-1.5" />Record {sel.size} payment{sel.size === 1 ? '' : 's'}</Button>
              )}
              {perms.has('export') && <DownloadMenu onPick={download} testId="batch-export" />}
              {b.status === 'draft' && perms.has('approve') && (del ? (
                <span className="flex items-center gap-2 ml-auto"><span className="text-xs text-red-600">Items go back to the ledger.</span>
                  <Button size="sm" variant="outline" onClick={() => setDel(false)}>Keep</Button>
                  <Button size="sm" disabled={busy} className="bg-red-600 hover:bg-red-700" onClick={remove} data-testid="batch-delete-confirm">Delete</Button></span>
              ) : <Button size="sm" variant="ghost" className="ml-auto text-slate-500 hover:text-red-600" onClick={() => setDel(true)} data-testid="batch-delete-btn"><Trash2 className="w-4 h-4 mr-1.5" />Delete</Button>)}
            </div>
            {paying && (
              <div className="rounded-xl border-2 border-emerald-200 bg-emerald-50/40 p-3 space-y-3" data-testid="batch-pay-form">
                <div className="text-sm font-semibold text-emerald-900">Paying {sel.size} official{sel.size === 1 ? '' : 's'} their full remaining balance · {fmtMoney(selTotal, currency)}</div>
                <MethodFields form={paying} set={(k, v) => setPaying(p => ({ ...p, [k]: v }))} testId="batch-pay" />
                <div className="flex justify-end gap-2">
                  <Button size="sm" variant="outline" onClick={() => setPaying(null)}>Cancel</Button>
                  <Button size="sm" disabled={busy || !sel.size} onClick={pay} className="bg-emerald-600 hover:bg-emerald-700" data-testid="batch-pay-confirm">
                    {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}Confirm payments</Button>
                </div>
              </div>
            )}
            <div className="border rounded-xl max-h-[42vh] overflow-y-auto">
              <div className="flex gap-3 px-3 py-2 bg-slate-50 text-[10px] uppercase font-bold text-slate-500 sticky top-0">
                <span className="w-5" /><span className="flex-1">Official</span><span className="w-24 text-right">Amount</span><span className="w-24 text-right">Paid</span><span className="w-24 text-right">Remaining</span>
              </div>
              {b.officials.map(r => <OfficialRow key={r.official_id} r={r} b={b} currency={currency} perms={perms} checked={sel.has(r.official_id)} onCheck={toggle}
                open={open === r.official_id} onOpen={() => setOpen(o => (o === r.official_id ? null : r.official_id))} onRemove={removeLine} busy={busy} />)}
              {!b.officials.length && <p className="py-8 text-center text-sm text-slate-500">This batch is empty.</p>}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
